import React from 'react';

import {Footnotes, Footnote, Ref} from '../util.js';

export default function() {
    return <>
        <p>A bit silly, you might say. What if we restrict ourselves to symmetric (even) potential functions?</p>
        <p>As intuition suggests, it turns out that the SHO is indeed the unique symmetric constant-period oscillator.
            We will denote the potential by \(U(x)\), and make the following assumptions:</p>
        <ul>
            <li>As discussed, we assume \(U(x) = U(-x)\).</li>
            <li>For simplicity, we set \(U(0) = 0\).</li>
            <li>Since we are doing physics, we assume that \(U(x)\) is differentiable.</li>
            <li>To avoid the particle getting stuck, we will assume that \(U(x)\) is strictly increasing on \(x > 0\).<Ref id={1}/></li>
        </ul>
        In what follows, we assume \(x > 0\).
        Suppose we launch the particle rightwards at time \(t = 0\) with kinetic energy \(E\).
        By time-symmetry, the particle must come to rest at time \(t = T/4\), where \(T\) is the period of the oscillator.
        Thus
        {String.raw`\[
        \begin{aligned}
            T &= 4 \int_0^{T/4} \dd{t} \\
            &= 4 \int_0^E \frac{\dd{t}}{\dd{x}} \frac{\dd{x}}{\dd{U}} \, \dd{U} \\
            &= 4 \int_0^E \frac{\dd{U}}{v \, U'(x)},
        \end{aligned}
        \]`}
        where \(v\) is the velocity of the particle.
        Since {'\\(E - U = \\frac{1}{2} m v^2\\)'} is the kinetic energy of the particle, we have
        {String.raw`\[
        \begin{aligned}
            T &\propto \int_0^E \frac{\dd{U}}{U'(U) \, \sqrt{E - U}} \\
            &= \int_0^E \frac{1}{\sqrt{U} \, \sqrt{E - U}} \underbrace{\frac{\sqrt{U}}{U'(U)}}_{\defeq f(U)} \, \dd{U}.
        \end{aligned}
        \]`}
        Making the substitution {'\\(U = E \\sin^2{\\theta}\\)'},
        we obtain
        {String.raw`\[
            T \propto \int_0^{\pi/2} f(E \sin^2{\theta}) \, \dd{\theta}.
        \]`}
        The proportionality constant throughout depends only on \(m\) and fixed numerical factors.
        If the oscillator is to have a fixed period,
        the integral
        {'\\[\\int_0^{\\pi/2} f(E \\sin^2{\\theta}) \\, \\dd{\\theta}\\]'}
        must be independent of \(E\).
        Under some mild continuity assumptions,
        it is a fun analysis exercise to show that \(f\) must be constant.<Ref id={2}/>
        But integrating the equality
        {String.raw`\[
        \frac{\dd{U}}{\sqrt{U}} = \frac{U'}{\sqrt{U}} \, \dd{x} = \frac{\dd{x}}{f(U)} = C \, \dd{x}
        \]`}
        yields {'\\(2 \\sqrt{U} = Cx\\)'},<Ref id={3}/>
        the harmonic oscillator potential.
        

        <Footnotes>
            <Footnote id={1}>
                This condition implies that \(U\) is a parameterization of \(x\) for \(x > 0\). We will frequently omit pullbacks and write \(f(U)\) instead of \(f(x(U))\).
            </Footnote>
            <Footnote id={2}>
                This makes intuitive sense; the integral is essentially a weighted average of \(f\) on the interval \([0, E]\).
                For all such averages to be equal, the function \(f\) must be constant.
            </Footnote>
            <Footnote id={3}>Recall that \(U(0) = 0\).</Footnote>
        </Footnotes>
    </>;

};
